import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';

import FinancialOverviewCards from './components/FinancialOverviewCards';
import FinancialCharts from './components/FinancialCharts';
import TransactionTable from './components/TransactionTable';
import FinancialSidebar from './components/FinancialSidebar';
import TransactionModal from './components/TransactionModal';

const FinancialManagement = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('last6Months');
  const [customDateRange, setCustomDateRange] = useState({ startDate: '', endDate: '' });
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Buscar hotel selecionado do localStorage
    const storedHotel = localStorage.getItem('selectedHotel');
    if (storedHotel) {
      try {
        setSelectedHotel(JSON.parse(storedHotel));
      } catch (error) {
        console.error('Erro ao parsear hotel selecionado:', error);
      }
    }
  }, []);

  const handleTransactionSuccess = () => {
    setRefreshKey(prev => prev + 1);
    setShowTransactionModal(false);
    setSelectedTransaction(null);
  };

  const handleAddTransaction = () => {
    setSelectedTransaction(null);
    setShowTransactionModal(true);
  };

  const handleEditTransaction = (transaction) => {
    setSelectedTransaction(transaction);
    setShowTransactionModal(true);
  };

  const handleCloseModal = () => {
    setShowTransactionModal(false);
    setSelectedTransaction(null);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header onMenuClick={() => setIsMobileMenuOpen(true)} />
      <Sidebar isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
      
      <main className="lg:ml-60 pt-16">
        <div className="p-6">
          <div className="max-w-7xl mx-auto">
            {/* Breadcrumb */}
            <Breadcrumb />
            
            {/* Page Header */}
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 mb-8">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl font-bold text-foreground">Gestão Financeira</h1>
                  {selectedHotel && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                      {selectedHotel.name}
                    </span>
                  )}
                </div>
                <p className="text-muted-foreground text-sm max-w-2xl">
                  Controle completo das finanças do seu hotel com relatórios detalhados e análises em tempo real
                </p>
              </div>
              
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={handleAddTransaction}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-all duration-200 shadow-sm hover:shadow-md active:scale-95"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span>Nova Transação</span>
                </button>
                
                <button
                  onClick={() => setRefreshKey(prev => prev + 1)}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-card text-foreground font-medium rounded-lg border border-border hover:bg-accent transition-all duration-200 hover:shadow-sm"
                  title="Atualizar dados"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
              {/* Main Content */}
              <div className="xl:col-span-3 space-y-6">
                {/* Financial Overview Cards */}
                <FinancialOverviewCards 
                  key={`overview-${refreshKey}`}
                  selectedPeriod={selectedPeriod}
                  selectedHotel={selectedHotel}
                />
                
                {/* Financial Charts */}
                <FinancialCharts 
                  key={`charts-${refreshKey}`}
                  selectedPeriod={selectedPeriod}
                  selectedHotel={selectedHotel}
                />
                
                {/* Transaction Table */}
                <TransactionTable 
                  key={`table-${refreshKey}`}
                  selectedPeriod={selectedPeriod}
                  selectedHotel={selectedHotel}
                  onEdit={handleEditTransaction}
                  onRefresh={() => setRefreshKey(prev => prev + 1)}
                />
              </div>

              {/* Right Sidebar */}
              <div className="xl:col-span-1">
                <FinancialSidebar
                  selectedPeriod={selectedPeriod}
                  setSelectedPeriod={setSelectedPeriod}
                  customDateRange={customDateRange}
                  setCustomDateRange={setCustomDateRange}
                  onAddTransaction={handleAddTransaction}
                  refreshTrigger={refreshKey}
                />
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Transaction Modal */}
      <TransactionModal
        isOpen={showTransactionModal}
        onClose={handleCloseModal}
        onSuccess={handleTransactionSuccess}
        transaction={selectedTransaction}
        selectedHotel={selectedHotel}
      />
    </div>
  );
};

export default FinancialManagement;